<template>
  <v-app
      class="container"
      :class="{ 'container-img': showBackground }"
      :style="containerStyle"
  >
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col :cols="12" :xs="12" :sm="8" :md="4" :lg="4">
          <v-card class="elevation-1 pa-3">
            <v-card-text>
              <v-form ref="form" v-model="valid">
                <div
                    v-if="showLoginLabel"
                    :style="loginLabelStyle"
                >
                  {{ loginLabelContent }}
                </div>
                <Input
                  label="Логин"
                  append-inner-icon="mdi-account"
                  v-model="username"
                  :disabled="loading"
                  :rules="validation.required()"
                  @keydown.enter="handleSubmit"
                />

                <Input
                  label="Пароль"
                  append-inner-icon="mdi-lock"
                  type="password"
                  v-model="password"
                  :disabled="loading"
                  :rules="validation.required()"
                  @keydown.enter="handleSubmit"
                />

                <v-dialog :model-value="isResetPassword">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-btn
                      v-bind="activatorProps"
                      class="reset-password-btn"
                      variant="plain"
                      hide-details
                      :text="$t('Забыли пароль?')"
                    />
                  </template>

                  <template v-slot:default="{ isActive }">
                    <v-card class="reset-password-card">
                      <v-card-text class="reset-password-card-text">
                        <v-text-field
                          v-if="!isResetPassword"
                          :label="$t('Адрес электронной почты')"
                          :placeholder="$t('Введите E-mail')"
                          class="required"
                          variant="outlined"
                          v-model="email"
                          :rules="[].concat(validation.required(), validation.text(255), validation.email())"
                        />
                        <div v-else>
                          <v-form v-model="validRessetPassword">
                            <v-text-field     
                              :label="$t('Новый пароль')"
                              :placeholder="$t('Введите пароль')"
                              variant="outlined"
                              v-model="newPassword"
                              :type="showNewPassword ? 'text' : 'password'"
                              :rules="[].concat(getPasswordRules())"
                              :hide-details="isActivePasswordPolicy"
                              :append-inner-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                              @click:append-inner="showNewPassword = !showNewPassword"
                            />
                            <div style="font-size: 12px;" v-if="isActivePasswordPolicy">
                              <span v-for="rule in passwordRules" :class="rule.class">{{rule.text}}</span>
                            </div>
                            <v-text-field   
                              class="mt-4"                  
                              :label="$t('Подтверждение пароля')"
                              :placeholder="$t('Введите пароль')"
                              variant="outlined"
                              v-model="confirmPassword"
                              :type="showConfirmPassword ? 'text' : 'password'"
                              :rules="[].concat(validation.required(), validation.text(255), validation.equal(newPassword, 'Пароли не совпадают'))"
                              :append-inner-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                              @click:append-inner="showConfirmPassword = !showConfirmPassword"
                            />
                          </v-form>
                        </div>
                      </v-card-text>
                      <v-card-actions :class="{'reset-password-card-actions': !isResetPassword}">
                        <v-btn
                          v-if="!isResetPassword"
                          color="primary"
                          density="comfortable"
                          variant="outlined"
                          :text="$t('Назад')"
                          @click="isActive.value = false"
                        />

                        <v-btn
                          v-if="!isResetPassword"
                          color="primary"
                          density="comfortable"
                          variant="elevated"
                          :text="$t('Отправить')"
                          @click="sendResetPassword(isActive)"
                        />
                        <v-btn
                          v-else
                          color="primary"
                          density="comfortable"
                          variant="elevated"
                          :text="$t('Сохранить')"
                          @click="sendResetPassword(isActive)"
                        />
                      </v-card-actions>
                    </v-card>
                  </template>
                </v-dialog>
              </v-form>

              <span style="white-space: pre;">{{ message }}</span>
            </v-card-text>
            <v-card-actions class="d-flex justify-space-between">
              <v-btn
                color="primary"
                density="comfortable"
                variant="outlined"
                @click="handleRegister"
                :loading="loading"
              >{{ $t('Регистрация') }}</v-btn>
              <v-btn
                color="primary"
                density="comfortable"
                variant="elevated"
                @click="handleSubmit"
                :loading="loading"
              >{{ $t('Войти') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, onMounted, inject, computed } from 'vue';
import Config from '@/config';
import Input from "@intec/vue-components/form/Input.vue";
import validation from '@/components/validation';
import api from '@/api';
import { useRouter } from 'vue-router';
import { toast } from '@/lib/toast';
import {useHardPasswordRules} from "@/lib/composable";
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const isActivePasswordPolicy = Config.getConfig('PASSWORD_POLICY')?.value;
const {validatePassword, passwordRules} = useHardPasswordRules();
const validRessetPassword = ref(true)
const valid = ref(true);
const loading = ref(false);
const username = ref('');
const password = ref('');
const email = ref('');
const newPassword = ref('');
const showNewPassword = ref(false);
const confirmPassword = ref('');
const showConfirmPassword = ref(false);
const isResetPassword = ref(false);
const message = ref('');
const router = useRouter();
const themeData = inject('themeData');

const showBackground = computed(() =>
    !!themeData?.auth?.backgroundStyle
);

const containerStyle = computed(() =>
    themeData?.auth?.backgroundStyle ?? ''
);

const showLoginLabel = computed(() =>
    !!themeData?.auth?.loginLabel
);

const loginLabelStyle = computed(() =>
    themeData?.auth?.loginLabel?.style ?? ''
);

const loginLabelContent = computed(() =>
    themeData?.auth?.loginLabel?.content ?? ''
);

const getPasswordRules = () => {
  return isActivePasswordPolicy ? validatePassword()
    : [].concat(validation.required(), validation.text(255));
}

const handleSubmit = async () => {
  if (valid.value) {
    try {
      loading.value = true;

      await api.post('/auth/login', {
        username: username.value,
        password: password.value,
      });

      // Первый вход после авторизации
      sessionStorage.setItem('is_first_login', true);
      window.location.reload();
    } catch (e) {
      message.value = t(e.message || 'Ошибка при входе.');
      loading.value = false;
    }
  }
};

const sendResetPassword = async (isActive) => {
  try {
    let formData = new FormData();

    if (isResetPassword.value) {
      const hashReset = router.currentRoute.value.params.hash;
                  
      if (newPassword.value === '' || confirmPassword.value === '') {
        toast.error('Заполните все обязательные поля');
        return false;
      }

      if (!validRessetPassword.value) {
        toast.error('Проверьте правильность заполнения формы');
        return false;
      }

      formData.append('data', JSON.stringify({
        newPassword: newPassword.value,
        hash: hashReset
      }));
      try {
        await api.post(`/auth/resetPassword/${hashReset}`, formData);
        toast.info('Пароль успешно изменен');
        setTimeout(() => {
          window.location.href = '/#';
          window.location.reload();
        }, 1000);
      } catch (e) {
        toast.error(e.message);
      }
    } else {
      formData.append('data', JSON.stringify({email: email.value}));
      await api.post('/auth/resetPassword', formData);
      isActive.value = false;
    }
  } catch (e) {
    toast.error(e.message);
  }
}

const handleRegister = function() {
  router.push({name: 'register'});
};

onMounted(() => {  
  if (router.currentRoute.value.fullPath.includes('/auth/resetPassword')) {
    isResetPassword.value = true;
  }
  
  const user = Config.get('user');

  if (user) {
    const ref = sessionStorage.getItem('redirectUrlAfterLogin');

    if (ref && ref !== window.location.href) {
      sessionStorage.removeItem('redirectUrlAfterLogin');

      router.replace(ref);
    } else {
      router.replace('/');
    }
  }
});
</script>

<style lang="scss" scoped>
.container {
  height: 50%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  content: '';
  z-index: 0;
  background-color: rgb(var(--primary-color));

  &-img {
    height: 100%;
  }
}

.reset-password {
  &-card {
    align-self: center;
    width: 450px;
    padding: 20px;
    &-text, &-actions {
      padding: 0px !important;
    }
    &-actions {
      justify-content: space-between !important;
      margin-top: 10px;
    }
  }
  &-btn {
    display: flex;
    justify-self: flex-end;
    height: fit-content;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
}

.required {
  :deep(.v-label::after) {
    content: ' *';
    color: rgba(var(--error-color));
  }
}
</style>
